import { getCompatibleStyle, unsafeCSS } from 'lit';
import legibleDisabledCss from './shoelace-legible-disabled.css?inline';
import checkboxBorderCss from './shoelace-checkbox-border.css?inline';
import type { CSSResult, CSSResultGroup, CSSResultOrNative } from 'lit';
import SlInput from '@shoelace-style/shoelace/dist/components/input/input.component.js';
import SlSelect from '@shoelace-style/shoelace/dist/components/select/select.component.js';
import SlTextarea from '@shoelace-style/shoelace/dist/components/textarea/textarea.component.js';
import SlCheckbox from '@shoelace-style/shoelace/dist/components/checkbox/checkbox.component.js';

/**
 * App-wide Shoelace control overrides, applied once at bootstrap so they reach
 * EVERY control instance across the whole app - now and for any future page -
 * with no per-component wiring.
 *
 * Why this lever: Shoelace controls live in their own shadow roots, so
 * document-level CSS (global.css) cannot reach them; the values we need to
 * change are not exposed as `:root` design tokens; and there is no shared UI
 * base class to centralise on (most components extend `LitElement` directly).
 * Adding a stylesheet to each Shoelace class itself is the only lever that is
 * genuinely application-wide.
 *
 * What it changes (the rules and their rationale live in the two sibling
 * stylesheets):
 *  1. Disabled-field legibility. Shoelace dims disabled controls to
 *     `opacity: 0.5`, which makes their DATA hard to read. This is a data
 *     system: the tinted disabled background + not-allowed cursor already signal
 *     "uneditable", so we restore full opacity and keep the data legible. The
 *     disabled text colour needs no change: Shoelace's `--sl-input-color-disabled`
 *     is neutral-900 in both the light and dark themes index.html loads (2.16.0,
 *     and the same in the installed 2.20.1), so opacity is all that needs
 *     restoring.
 *  2. Checkbox visibility. Shoelace's unchecked checkbox border
 *     (`--sl-input-border-color`, neutral-300) is too faint to see reliably
 *     across monitors; the control border takes the `--agwa-border-strong`
 *     role, the edge that must read against a busy background. Checked boxes
 *     keep their higher-specificity primary border.
 *
 * The mechanism, correct in either registration order. Lit reads a class's
 * static `styles` exactly once, in `static finalize()` when the tag is
 * registered, and copies the result to `elementStyles`
 * (@lit/reactive-element 2.1.2, development/reactive-element.js:416); every
 * instance then adopts `this.constructor.elementStyles` into its shadow root in
 * `createRenderRoot` (:567). So `appendStyles` does two things:
 *  - it always appends to the static `styles`, which is what a class that is
 *    not finalised yet reads at registration (the unbundled dev server's order,
 *    and any subclass of these classes finalised later, which inherits
 *    `styles`);
 *  - when the class is already finalised, it also pushes the style onto that
 *    class's own `elementStyles`, so every instance connected afterwards (its
 *    shadow root created) adopts it (the served bundle's order).
 * "Already finalised" is the class's OWN `finalized` property, which
 * `finalize()` sets and tests with `hasOwnProperty` (:382-385). Both checks are
 * own-property checks because an unfinalised class inherits an ancestor's
 * `elementStyles` (ReactiveElement's base array at :1068 at the root), and
 * pushing onto an inherited list would add the rule to that ancestor and every
 * other class sharing it.
 *
 * Why the first version, which only appended to `styles`, was inert in the
 * served app: ui/vite.config.ts `manualChunks` puts every `@shoelace-style`
 * module in the `vendor` chunk, which the served bundle runs before any module
 * of main.ts, so each wrapper's `define` registers and finalises its class
 * before this module runs, and the append arrived after Lit had read `styles`.
 * The dev server serves modules unbundled in import order, where this module
 * runs first, so it showed full opacity on every developer's machine while
 * every user saw 0.5 (issue #380, @FORM-LEGIBLE-001).
 *
 * What it still needs: to run before the first control is CONNECTED (its
 * shadow root created). An instance adopts its class's `elementStyles` once, in
 * `createRenderRoot`, which runs from `connectedCallback` (:567, :574-576), not
 * from the constructor; an instance constructed earlier but connected later
 * still gets the override. main.ts imports it before the app shell renders
 * anything.
 *
 * Coupling notes: targets Shoelace v2 internal part classes (`.input`,
 * `.textarea`, `.select__combobox`, `.checkbox__control`) and the reflected
 * `disabled` host attribute; re-verify these on a Shoelace major upgrade. The
 * served-order branch rests on Lit internals: the `finalized` own-property
 * marker (protected in Lit's typings, hence the structural read here),
 * `elementStyles` being the list `createRenderRoot` adopts, and
 * `getCompatibleStyle` as the shape `finalizeStyles` stores; re-verify all
 * three on a Lit major upgrade.
 * apply-shoelace-overrides.spec.ts (served order) and its dev-order sibling go
 * red if either branch stops reaching a rendered field.
 */

const legibleDisabled = unsafeCSS(legibleDisabledCss);

const checkboxBorder = unsafeCSS(checkboxBorderCss);

/** The static members of a Lit class that this module reads and extends. */
interface StyledCtor {
  styles?: CSSResultGroup;
  elementStyles?: CSSResultOrNative[];
}

/**
 * Add one stylesheet to a Shoelace class so every instance connected after
 * this call (its shadow root created) adopts it, whether or not Lit has
 * finalised the class yet.
 */
function appendStyles(ctor: StyledCtor, extra: CSSResult): void {
  // Read at registration by a class not yet finalised, and inherited by any
  // subclass finalised later.
  const existing = ctor.styles;
  if (existing == null) {
    ctor.styles = extra;
  } else if (Array.isArray(existing)) {
    ctor.styles = [...existing, extra];
  } else {
    ctor.styles = [existing, extra];
  }

  // Already finalised: `styles` will never be read again for this class, so
  // the style goes straight onto the list its instances adopt. Pushed last, as
  // `finalizeStyles` would have placed an appended style, so it wins the
  // cascade the same way; converted by `getCompatibleStyle` so it has the shape
  // `finalizeStyles` stores and `adoptStyles` accepts.
  if (
    Object.prototype.hasOwnProperty.call(ctor, 'finalized') &&
    Object.prototype.hasOwnProperty.call(ctor, 'elementStyles') &&
    ctor.elementStyles
  ) {
    ctor.elementStyles.push(getCompatibleStyle(extra));
  }
}

appendStyles(SlInput, legibleDisabled);
appendStyles(SlSelect, legibleDisabled);
appendStyles(SlTextarea, legibleDisabled);
appendStyles(SlCheckbox, checkboxBorder);
